/*
 * Create Round — name, format, scoring mode, course/tee picker, points.
 * Web port of CreateRoundScreen.
 *
 * Course flow (matches the round-flow CourseScreen on mobile):
 *
 *   1) Lookup is a PURE API call. We hit /courses/search (proxies the
 *      GolfCourseAPI catalog). Picking a course just stashes its data in
 *      React state. NO DB writes happen during browsing.
 *
 *   2) "Enter Manually" is the escape hatch for courses the catalog
 *      doesn't know (private clubs, foreign courses). User types name,
 *      slope, rating, par. We synthesize default per-hole data.
 *
 *   3) Submit is the COMMIT point. We import the picked-or-typed course
 *      to the local DB (idempotent — re-importing returns the existing
 *      row), then create the round with the real DB courseId + teeId.
 *
 * Create Round is disabled until the user has either (a) picked a
 * course AND tee in search mode, or (b) filled all the required manual
 * fields. We do not let people create courseless rounds because every
 * downstream piece — handicap math, scoring, leaderboards — needs a
 * course and tee.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Flag, ChevronRight } from 'lucide-react';

import { api } from '@/lib/api';
import { useCourseLookup } from '@/hooks/useCourseLookup';
import { CourseLookupStatus } from '@/components/CourseLookupStatus';
import type { CourseResult, CatalogTee as APITee } from '@/lib/courseLookup';
import { encodeRoundSchedule } from '@/lib/roundSchedule';
import {
  buildImportHoleCard,
  type PickerHoleCardEntry,
} from '@/lib/courseHoleCard';
import { AppShell } from '@/components/AppShell';
import { InfoHint } from '@/components/InfoHint';
import { TeamStablefordSettings } from '@/components/TeamStablefordSettings';
import { FORMAT_OPTIONS, SCORING_MODE_LABELS, MethodHintBody, BaselineHintBody } from './_shared';
import { teamCupStablefordConfig, type FormatType, type ScoringMode, type StablefordConfig } from '@golf-app/shared';

const POINTS_PRESETS = [0.5, 1, 2];

// Same tee color palette as the round-flow CourseScreen.
const TEE_COLORS: Record<string, string> = {
  black: '#111111', blue: '#1a56db', white: '#e5e5e5', red: '#cc0000',
  gold: '#d4af37', silver: '#9ca3af', green: '#22c55e',
};
const teeDotColor = (n: string) => TEE_COLORS[(n || '').toLowerCase()] ?? '#555';

// Synthetic tee row used in the picker UI. The id here is a local key
// (`t0`, `t1`…) — we resolve it to a real DB tee id at submit time.
interface PickerTee {
  key: string;
  name: string;
  slope: number;
  rating: number;
  parTotal: number;
  holes: PickerHoleCardEntry[];
}

/** Generate a complete 9-hole or 18-hole card with a unique stroke-index order. */
function defaultHoles(totalPar: number): Array<{ par: number; handicap: number }> {
  const holeCount = totalPar < 54 ? 9 : 18;
  // Most common layout for the par totals we care about.
  const pars: number[] =
    totalPar === 72 ? [4,4,3,5,4,4,3,5,4, 4,4,3,5,4,4,3,5,4] :
    totalPar === 71 ? [4,4,3,5,4,4,3,5,4, 4,4,3,5,4,4,3,4,4] :
    totalPar === 70 ? [4,4,3,5,4,4,3,4,4, 4,4,3,5,4,4,3,4,4] :
    // Best-effort spread for non-standard pars.
    (() => {
      const base = Math.floor(totalPar / holeCount);
      let extra = totalPar - base * holeCount;
      return Array.from({ length: holeCount }, () => {
        if (extra > 0) { extra--; return base + 1; }
        return base;
      });
    })();
  return pars.map((par, i) => ({ par, handicap: i + 1 }));
}

export default function CreateRound() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [formatType, setFormatType] = useState<FormatType>('singles_match_play');
  const [scoringMode, setScoringMode] = useState<ScoringMode>('gross');
  const [stablefordConfig, setStablefordConfig] = useState<StablefordConfig>(() => teamCupStablefordConfig());
  const [stablefordValid, setStablefordValid] = useState(true);
  const [pointsPerMatch, setPointsPerMatch] = useState('1');

  // Per-round handicap overrides. Empty string = inherit the event default;
  // a real value pins it just for this round. Most rounds will leave the
  // first option ('inherit') selected.
  const [handicapMethod, setHandicapMethod] = useState<'' | 'course' | 'straight'>('');
  const [allowancePctOverride, setAllowancePctOverride] = useState<string>(''); // '' = inherit
  const [baselineMethod, setBaselineMethod] = useState<'' | 'low_man_zero' | 'full_difference'>('');

  // Optional scheduling. Date stays empty by default. Session is "default"
  // (no session marker), "morning" (07:30 local), or "afternoon" (13:00).
  // Both are optional. Most rounds will leave them blank.
  const [scheduledDate, setScheduledDate] = useState('');
  const [session, setSession] = useState<'default' | 'morning' | 'afternoon'>('default');

  // ── Course state ────────────────────────────────────────────────────────
  // mode toggle: catalog search (default) or manual entry.
  const [courseMode, setCourseMode] = useState<'search' | 'manual'>('search');

  const courseLookup = useCourseLookup<{
    name: string; city: string | null; state: string | null; tees: PickerTee[];
  }>();
  const { query: courseQuery, results: courseResults, selected: pickedCourse, lookup } = courseLookup;
  const [teeKey, setTeeKey] = useState<string>('');
  useEffect(() => { setTeeKey(pickedCourse?.tees[0]?.key ?? ''); }, [pickedCourse]);

  // Manual-mode state.
  const [manualName, setManualName] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualState, setManualState] = useState('');
  const [manualTeeName, setManualTeeName] = useState('White');
  const [manualSlope, setManualSlope] = useState('');
  const [manualRating, setManualRating] = useState('');
  const [manualPar, setManualPar] = useState('72');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function changeCourseMode(mode: 'search' | 'manual') {
    lookup.setEnabled(mode === 'search');
    setCourseMode(mode);
    setError(null);
  }

  // Picking a course. The branch matters: local-DB courses come back
  // already in our schema (UUID tee ids with tee-owned cards), so we fetch
  // them via /courses/:id and map straight to PickerTee. Older API versions
  // expose only the legacy course card, which remains the fallback. API
  // courses are in GolfCourseAPI's shape — we have the
  // raw object cached from the search step and convert in place.
  async function pickCourse(row: CourseResult) {
    setError(null);
    await lookup.select(row, (course, full) => {
      if (full) {
        const localTees: PickerTee[] = (full.tees ?? [])
          .map((t, i) => ({
            key: `t${i}`,
            name: t.name,
            slope: t.slope,
            rating: t.rating,
            parTotal: t.parTotal,
            holes: (t.holes?.length ? t.holes : full.holes ?? []).map((h) => ({
              number: h.holeNumber,
              par: h.par,
              handicap: Math.min(18, Math.max(1, h.strokeIndex ?? h.holeNumber)),
              ...(h.yards == null ? {} : { yards: h.yards }),
            })),
          }))
          .sort((a, b) => b.rating - a.rating);
        if (localTees.length === 0) throw new Error(`"${course.name}" has no tee data we can use. Try another course or enter it manually.`);
        return { name: course.name, city: course.city, state: course.state, tees: localTees };
      }
      const apiCourse = course.catalog;
      if (!apiCourse) throw new Error('That catalog course is no longer available. Search again.');

      // Default to male tees, fall back to female. (Web has no gender field
      // yet; when it lands, branch here.)
      const sourceTees: APITee[] =
        (apiCourse.tees?.male?.length ? apiCourse.tees.male : apiCourse.tees?.female) ?? [];

      if (!sourceTees.length) {
        throw new Error(`"${course.name}" has no tee data we can use. Try another course or enter it manually.`);
      }

      // Strip women's tees from the picker, sort hardest first.
      const picker: PickerTee[] = sourceTees
        .filter((t) => !/\(w\)/i.test(t.tee_name))
        .map((t, i) => ({
          key: `t${i}`,
          name: t.tee_name,
          slope: t.slope_rating,
          rating: t.course_rating,
          parTotal: t.par_total,
          holes: (t.holes ?? []).map((h, idx) => ({
            number: h.hole_number ?? (idx + 1),
            par: h.par,
            // Cap handicap at 18 in case the API ever sends a duplicate or
            // out-of-range value; the DB CHECK enforces 1..18.
            handicap: Math.min(18, Math.max(1, h.handicap ?? (idx + 1))),
            ...(h.yards == null ? {} : { yards: h.yards }),
          })),
        }))
        .sort((a, b) => b.rating - a.rating);

      // If everything got filtered out (rare — course has only women's tees),
      // fall back to the unfiltered list rather than leaving the user stuck.
      const finalTees = picker.length > 0
        ? picker
        : sourceTees.map((t, i) => ({
            key: `t${i}`,
            name: t.tee_name,
            slope: t.slope_rating,
            rating: t.course_rating,
            parTotal: t.par_total,
            holes: (t.holes ?? []).map((h, idx) => ({
              number: h.hole_number ?? (idx + 1),
              par: h.par,
              handicap: Math.min(18, Math.max(1, h.handicap ?? (idx + 1))),
              ...(h.yards == null ? {} : { yards: h.yards }),
            })),
          }));

      return { name: course.name, city: course.city, state: course.state, tees: finalTees };
    });
  }

  function clearPickedCourse() {
    lookup.clear();
    setTeeKey('');
  }

  // ── Validation ──────────────────────────────────────────────────────────
  // Search mode is valid when course is picked AND a tee is selected.
  // Manual mode is valid when all required numeric fields parse cleanly.
  const manualSlopeNum = parseInt(manualSlope, 10);
  const manualRatingNum = parseFloat(manualRating);
  const manualParNum = parseInt(manualPar, 10);
  const manualValid =
    manualName.trim().length > 0 &&
    Number.isFinite(manualSlopeNum) && manualSlopeNum >= 55 && manualSlopeNum <= 155 &&
    Number.isFinite(manualRatingNum) && manualRatingNum >= 50 && manualRatingNum <= 90 &&
    Number.isFinite(manualParNum) && manualParNum >= 27 && manualParNum <= 80 &&
    manualTeeName.trim().length > 0;

  const courseValid =
    courseMode === 'search'
      ? Boolean(pickedCourse) && Boolean(teeKey)
      : manualValid;

  const ppmNum = parseFloat(pointsPerMatch);
  const formValid =
    name.trim().length > 0 &&
    Number.isFinite(ppmNum) && ppmNum > 0 &&
    courseValid &&
    (scoringMode !== 'stableford' || stablefordValid);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!eventId) return;
    if (!formValid) {
      setError(courseValid ? 'Please fix the highlighted fields.' : 'Add a course before creating the round.');
      return;
    }

    // Compose the optional scheduled start via the shared encoder:
    // Morning 07:30, Afternoon 13:00, Default 12:00 (sentinel — shows
    // no session label). AM still sorts before PM within a date.
    let scheduledStartAt: string | undefined;
    if (scheduledDate) {
      scheduledStartAt = encodeRoundSchedule(scheduledDate, session);
    }

    setSubmitting(true);
    try {
      // Step 1: persist the course (idempotent). This is the FIRST DB
      // touch in the whole flow. If we error here, the round is not yet
      // created, so retrying is safe.
      let courseId: string;
      let teeId: string;

      if (courseMode === 'search' && pickedCourse) {
        const selectedTee = pickedCourse.tees.find((t) => t.key === teeKey);
        if (!selectedTee) throw new Error('Pick a tee first.');
        const orderedTees = [
          selectedTee,
          ...pickedCourse.tees.filter((tee) => tee.key !== selectedTee.key),
        ];

        const imported = await api.importCourse({
          name: pickedCourse.name,
          city: pickedCourse.city,
          state: pickedCourse.state,
          selectedTeeName: selectedTee.name,
          // Persist ALL tees, not just the picked one — so future rounds at
          // this course don't need to re-search the catalog.
          tees: orderedTees.map((t) => ({
            name: t.name, slope: t.slope, rating: t.rating, parTotal: t.parTotal,
            holes: buildImportHoleCard(t.holes, defaultHoles(t.parTotal)),
          })),
        });

        courseId = imported.courseId;
        const dbTee = imported.tees.find((t) => t.name === selectedTee.name) ?? imported.tees[0];
        teeId = dbTee.id;
      } else {
        // Manual mode.
        const holes = defaultHoles(manualParNum).map((h, i) => ({
          number: i + 1, par: h.par, handicap: h.handicap,
        }));
        const imported = await api.importCourse({
          name: manualName.trim(),
          city: manualCity.trim() || null,
          state: manualState.trim() || null,
          tees: [{
            name: manualTeeName.trim(),
            slope: manualSlopeNum,
            rating: manualRatingNum,
            parTotal: manualParNum,
            holes,
          }],
        });
        courseId = imported.courseId;
        // Find by name (case-insensitive) because dedup might return tees
        // that were already attached to a previously imported same-named course.
        const dbTee = imported.tees.find((t) => t.name.toLowerCase() === manualTeeName.trim().toLowerCase()) ?? imported.tees[0];
        teeId = dbTee.id;
      }

      // Allowance % override needs to parse to a real number if entered,
      // otherwise we leave it null so the round inherits the event default.
      let allowancePctValue: number | null = null;
      if (allowancePctOverride.trim() !== '') {
        const n = parseFloat(allowancePctOverride);
        if (!Number.isFinite(n) || n < 0 || n > 200) {
          setError('Allowance % must be a number between 0 and 200.');
          setSubmitting(false);
          return;
        }
        allowancePctValue = n;
      }

      // Step 2: create the round.
      const r = await api.createTournamentRound(eventId, {
        name: name.trim(),
        formatType, scoringMode, pointsPerMatch: ppmNum,
        ...(scoringMode === 'stableford' ? { stablefordConfig } : {}),
        courseId, defaultTeeId: teeId,
        scheduledStartAt,
        handicapMethod: handicapMethod || null,
        allowancePct: allowancePctValue,
        baselineMethod: baselineMethod || null,
      });
      navigate(`/app/tournaments/${eventId}/rounds/${r.id}`, { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create round');
      setSubmitting(false);
    }
  }

  const selectFormat = useCallback((next: FormatType) => {
    setFormatType(next);
    if (scoringMode === 'stableford' && next !== 'aggregate_match_play') setScoringMode('net');
  }, [scoringMode]);

  const selectScoringMode = useCallback((next: ScoringMode) => {
    setScoringMode(next);
    if (next === 'stableford') setFormatType('aggregate_match_play');
  }, []);

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}/rounds`, label: 'Rounds' }}>
      <section className="t-hero">
        <h1>New Round</h1>
        <p>Pick the format, course, and tee. You can build matchups next.</p>
      </section>

      {error && <div className="t-error">{error}</div>}

      <form className="t-form" onSubmit={handleSubmit}>
        <div className="t-field">
          <label htmlFor="r-name" className="t-label">Round name</label>
          {/* No autoFocus: on phones it pops the keyboard over the form
              the instant the page opens, hiding the steps below. */}
          <input id="r-name" className="t-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Friday AM Four-Ball" />
        </div>

        <div className="t-field">
          <div className="t-label">Format</div>
          {FORMAT_OPTIONS.map((f) => (
            <button key={f.value} type="button" className={`t-option ${formatType === f.value ? 't-option-selected' : ''}`} onClick={() => selectFormat(f.value)}>
              <span className="t-option-radio">{formatType === f.value && <span className="t-option-radio-dot" />}</span>
              <span>
                <p className={`t-option-label ${formatType === f.value ? 't-option-label-selected' : ''}`}>{f.label}</p>
                <p className="t-option-hint">{f.hint}</p>
              </span>
            </button>
          ))}
        </div>

        <div className="t-field">
          <div className="t-label">Scoring</div>
          <div className="t-chip-row">
            {(['gross', 'net', 'gross_and_net', 'stableford'] as ScoringMode[]).map((m) => (
              <button key={m} type="button" className={`t-chip ${scoringMode === m ? 't-chip-active' : ''}`} onClick={() => selectScoringMode(m)}>
                {SCORING_MODE_LABELS[m]}
              </button>
            ))}
          </div>
          {scoringMode === 'stableford' ? <p className="t-hint">Stableford uses Aggregate, includes every assigned player, and lets each group save its own scores.</p> : null}
        </div>

        {scoringMode === 'stableford' ? (
          <div className="t-field scorecard-stableford-card">
            <div className="t-label">Team Stableford points</div>
            <TeamStablefordSettings
              value={stablefordConfig}
              onChange={setStablefordConfig}
              onValidityChange={setStablefordValid}
            />
          </div>
        ) : null}

        {scoringMode !== 'stableford' ? <div className="t-field">
          <div className="t-label">Points per match</div>
          <div className="t-chip-row">
            {POINTS_PRESETS.map((p) => (
              <button key={p} type="button" className={`t-chip ${parseFloat(pointsPerMatch) === p ? 't-chip-active' : ''}`} onClick={() => setPointsPerMatch(String(p))}>
                {p}
              </button>
            ))}
          </div>
        </div> : null}

        {/* ── Handicap overrides ──────────────────────────────────────────
            Three optional pickers that only apply to this round. Default
            choice for every one is "Inherit event default" — most rounds
            ride on the event-level settings.  Each picker has an (i)
            info button with a worked example. */}
        <div className="t-field">
          <div className="t-label" style={{ display: 'flex', alignItems: 'center' }}>
            Handicap method (this round)
            <InfoHint title="Handicap method" body={<MethodHintBody />} />
          </div>
          <div className="t-chip-row">
            {([
              { value: '' as const,         label: 'Inherit event default' },
              { value: 'course' as const,   label: 'Net Course Rating' },
              { value: 'straight' as const, label: 'Net Straight Handicap' },
            ]).map((opt) => (
              <button key={opt.value || 'inherit'} type="button"
                className={`t-chip ${handicapMethod === opt.value ? 't-chip-active' : ''}`}
                onClick={() => setHandicapMethod(opt.value)}>{opt.label}</button>
            ))}
          </div>
        </div>

        <div className="t-field">
          <label htmlFor="r-allowance" className="t-label" style={{ display: 'flex', alignItems: 'center' }}>
            Allowance % (this round)
            <InfoHint
              title="Match-play allowance"
              body="A percentage applied to the computed handicap before netting. Lower allowance = closer match. Leave blank to inherit the event default. USGA recommends 100% (full), 90% (Singles), 85% (Four-Ball)."
              example={
                <>
                  A handicap of 14 at <strong>100%</strong> plays as 14, at <strong>90%</strong> as 13,
                  at <strong>85%</strong> as 12.
                </>
              }
            />
          </label>
          <input
            id="r-allowance"
            className="t-input"
            value={allowancePctOverride}
            onChange={(e) => setAllowancePctOverride(e.target.value)}
            inputMode="decimal"
            placeholder="Leave blank to inherit"
          />
        </div>

        <div className="t-field">
          <div className="t-label" style={{ display: 'flex', alignItems: 'center' }}>
            Baseline (netting, this round)
            <InfoHint title="Baseline (netting)" body={<BaselineHintBody />} />
          </div>
          <div className="t-chip-row">
            {([
              { value: '' as const,                  label: 'Inherit event default' },
              { value: 'low_man_zero' as const,      label: 'Low man zero' },
              { value: 'full_difference' as const,   label: 'Full difference' },
            ]).map((opt) => (
              <button key={opt.value || 'inherit'} type="button"
                className={`t-chip ${baselineMethod === opt.value ? 't-chip-active' : ''}`}
                onClick={() => setBaselineMethod(opt.value)}>{opt.label}</button>
            ))}
          </div>
        </div>

        {/* Optional scheduling. Both fields blank by default. The Session row
            is for the rare 36-hole day where one event has a Morning round and
            an Afternoon round on the same date. */}
        <div className="t-field-row">
          <div className="t-field">
            <label htmlFor="r-date" className="t-label">Date (optional)</label>
            <input
              id="r-date"
              type="date"
              className="t-input"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              onClick={(e) => { try { (e.currentTarget as HTMLInputElement).showPicker(); } catch {} }}
            />
          </div>
          <div className="t-field">
            <div className="t-label">Session (optional)</div>
            <div className="t-chip-row">
              {([
                { key: 'default',   label: 'Default'   },
                { key: 'morning',   label: 'Morning'   },
                { key: 'afternoon', label: 'Afternoon' },
              ] as { key: typeof session; label: string }[]).map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={`t-chip ${session === s.key ? 't-chip-active' : ''}`}
                  onClick={() => setSession(s.key)}
                  disabled={!scheduledDate && s.key !== 'default'}
                  title={!scheduledDate && s.key !== 'default' ? 'Pick a date first' : undefined}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Course block. Required. Two modes: search the catalog or enter
            manually. The Create Round button stays disabled until either
            mode produces a valid selection. */}
        <div className="t-field">
          <div className="t-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Course<span style={{ color: 'var(--green-bright)', marginLeft: 6 }}>*</span></span>
            <div className="t-chip-row" style={{ gap: 6 }}>
              <button
                type="button"
                className={`t-chip ${courseMode === 'search' ? 't-chip-active' : ''}`}
                style={{ padding: '10px 14px', fontSize: 13, minHeight: 40 }}
                onClick={() => changeCourseMode('search')}
              >
                Search
              </button>
              <button
                type="button"
                className={`t-chip ${courseMode === 'manual' ? 't-chip-active' : ''}`}
                style={{ padding: '10px 14px', fontSize: 13, minHeight: 40 }}
                onClick={() => changeCourseMode('manual')}
              >
                Enter Manually
              </button>
            </div>
          </div>

          {courseMode === 'search' ? (
            pickedCourse ? (
              <div className="t-row" style={{ cursor: 'default', borderColor: 'var(--green-bright)' }}>
                <div className="t-row-icon t-row-icon-green"><Flag /></div>
                <div className="t-row-body">
                  <div className="t-row-name">{pickedCourse.name}</div>
                  {(pickedCourse.city || pickedCourse.state) && (
                    <div className="t-row-sub">{[pickedCourse.city, pickedCourse.state].filter(Boolean).join(', ')}</div>
                  )}
                </div>
                <button type="button" className="t-btn t-btn-ghost" onClick={clearPickedCourse}>
                  Clear
                </button>
              </div>
            ) : (
              <>
                <input
                  id="r-course"
                  className="t-input"
                  placeholder="Search any course by name or city…"
                  value={courseQuery}
                  onChange={(e) => lookup.setQuery(e.target.value)}
                  aria-label="Search course or city"
                  autoComplete="off" autoCorrect="off" spellCheck={false}
                />
                <CourseLookupStatus state={courseLookup} onRetry={lookup.retry} />
                {courseResults.length > 0 && (
                  <div className="t-list" style={{ marginTop: 8 }}>
                    {courseResults.map((c) => (
                      <button type="button" key={`${c.source}-${c.id}`} className="t-row" onClick={() => pickCourse(c)}>
                        <div className="t-row-body">
                          <div className="t-row-name">{c.name}</div>
                          {(c.city || c.state) && <div className="t-row-sub">{[c.city, c.state].filter(Boolean).join(', ')}</div>}
                        </div>
                        <div className="t-row-chevron"><ChevronRight /></div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )
          ) : (
            // Manual entry mode.
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                className="t-input"
                placeholder="Course name (e.g. Big Cedar Lodge - Mountain Top)"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
              />
              <div className="t-field-row">
                <input
                  className="t-input"
                  placeholder="City (optional)"
                  value={manualCity}
                  onChange={(e) => setManualCity(e.target.value)}
                />
                <input
                  className="t-input"
                  placeholder="State (optional)"
                  value={manualState}
                  onChange={(e) => setManualState(e.target.value)}
                  maxLength={3}
                  style={{ maxWidth: 100 }}
                />
              </div>
              <div className="t-field-row">
                <input
                  className="t-input"
                  placeholder="Tee name (e.g. White)"
                  value={manualTeeName}
                  onChange={(e) => setManualTeeName(e.target.value)}
                />
                <input
                  className="t-input"
                  placeholder="Par"
                  type="number"
                  inputMode="numeric"
                  value={manualPar}
                  onChange={(e) => setManualPar(e.target.value)}
                  style={{ maxWidth: 100 }}
                />
              </div>
              <div className="t-field-row">
                <input
                  className="t-input"
                  placeholder="Slope (55–155)"
                  type="number"
                  inputMode="numeric"
                  value={manualSlope}
                  onChange={(e) => setManualSlope(e.target.value)}
                />
                <input
                  className="t-input"
                  placeholder="Course rating (50.0–90.0)"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={manualRating}
                  onChange={(e) => setManualRating(e.target.value)}
                />
              </div>
              <p className="t-hint" style={{ marginTop: 4 }}>
                We'll generate a default 18-hole layout from the par total. You can refine per-hole pars later.
              </p>
            </div>
          )}
        </div>

        {courseMode === 'search' && pickedCourse && pickedCourse.tees.length > 0 && (
          <div className="t-field">
            <div className="t-label">Default tee<span style={{ color: 'var(--green-bright)', marginLeft: 6 }}>*</span></div>
            {pickedCourse.tees.map((t) => {
              const sel = teeKey === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTeeKey(t.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    borderRadius: 8, border: '1px solid var(--border)',
                    padding: '9px 10px', marginBottom: 5,
                    background: sel ? '#0f2a1e' : 'var(--input)',
                    borderColor: sel ? 'var(--green-bright)' : 'var(--border)',
                    cursor: 'pointer', width: '100%',
                  }}
                >
                  <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: teeDotColor(t.name), border: '1px solid rgba(255,255,255,0.2)' }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: sel ? 'var(--green-bright)' : 'var(--text)' }}>{t.name}</span>
                  <span style={{ flex: 1, fontSize: 12, color: sel ? 'rgba(34,197,94,0.7)' : 'var(--text-sec)', textAlign: 'right' }}>
                    {t.rating} / {t.slope} · Par {t.parTotal}
                  </span>
                  {sel && <span style={{ fontSize: 14, color: 'var(--green-bright)', fontWeight: 800 }}>✓</span>}
                </button>
              );
            })}
          </div>
        )}

        <button
          type="submit"
          className="t-btn t-btn-primary t-btn-block"
          disabled={submitting || !formValid}
          title={!formValid ? 'Add a course and pick a tee to enable Create Round' : undefined}
        >
          {submitting ? 'Creating…' : 'Create Round'}
        </button>
        {!courseValid && (
          <p className="t-hint" style={{ marginTop: 8, textAlign: 'center' }}>
            {courseMode === 'search'
              ? (pickedCourse ? 'Pick a tee to continue.' : 'Search for a course or switch to manual entry.')
              : 'Fill in course name, tee, slope, rating, and par.'}
          </p>
        )}
      </form>
    </AppShell>
  );
}
